import { ASSETS } from '../config';

export type GameThemeId = 'forest-moving' | 'gothic';
export type ThemedAssetFormat = 'svg' | 'png';

export type ShadowKind = 'block' | 'wall' | 'actor' | 'round';

export interface ThemeShadowStyle {
  ox: number;
  oy: number;
  width: number;
  height: number;
  alpha: number;
}

export interface GameThemeDefinition {
  id: GameThemeId;
  name: string;
  svgDir: string;
  pngDir: string;
  shadow: Record<ShadowKind, ThemeShadowStyle>;
  assetFormats?: Partial<Record<string, ThemedAssetFormat>>;
  assetOverrides?: Partial<Record<string, string>>;
}

const FOREST_MOVING_THEME: GameThemeDefinition = {
  id: 'forest-moving',
  name: 'Forest Moving Company',
  svgDir: 'assets/svg',
  pngDir: 'assets/png',
  assetFormats: {
    [ASSETS.GRASS_FRAME]: 'png',
  },
  assetOverrides: {
    [ASSETS.GRASS_FRAME]: 'assets/themes/forest-moving/png/grass-frame.png',
  },
  shadow: {
    block: { ox: 9, oy: 18, width: 1.12, height: 0.5, alpha: 0.62 },
    wall: { ox: 8, oy: 18, width: 1.1, height: 0.48, alpha: 0.52 },
    actor: { ox: 5, oy: 18, width: 0.78, height: 0.32, alpha: 0.44 },
    round: { ox: 7, oy: 17, width: 0.88, height: 0.38, alpha: 0.48 },
  },
};

const GOTHIC_THEME: GameThemeDefinition = {
  id: 'gothic',
  name: 'Gothic Warehouse',
  svgDir: 'assets/themes/gothic/svg',
  pngDir: 'assets/themes/gothic/png',
  shadow: {
    block: { ox: 10, oy: 19, width: 1.14, height: 0.52, alpha: 0.7 },
    wall: { ox: 10, oy: 19, width: 1.16, height: 0.52, alpha: 0.66 },
    actor: { ox: 6, oy: 19, width: 0.8, height: 0.34, alpha: 0.52 },
    round: { ox: 8, oy: 18, width: 0.9, height: 0.4, alpha: 0.56 },
  },
  assetOverrides: {
    // Future gothic assets can be dropped into public/assets/themes/gothic.
    [ASSETS.SHADOW_SOFT]: 'assets/svg/shadow-soft.svg',
    [ASSETS.GRASS_FRAME]: 'assets/themes/forest-moving/png/grass-frame.png',
  },
};

const THEMES: Record<GameThemeId, GameThemeDefinition> = {
  'forest-moving': FOREST_MOVING_THEME,
  gothic: GOTHIC_THEME,
};

let currentThemeId: GameThemeId = readInitialThemeId();

export function getCurrentGameTheme(): GameThemeDefinition {
  return THEMES[currentThemeId];
}

export function setCurrentGameTheme(themeId: GameThemeId): void {
  currentThemeId = themeId;
}

export function getThemedAssetUrl(assetId: string, format: ThemedAssetFormat): string {
  const theme = getCurrentGameTheme();
  const override = theme.assetOverrides?.[assetId];
  if (override) return override;
  const dir = format === 'png' ? theme.pngDir : theme.svgDir;
  return `${dir}/${assetId}.${format}`;
}

export function getThemedAssetFormat(assetId: string, fallback: ThemedAssetFormat): ThemedAssetFormat {
  return getCurrentGameTheme().assetFormats?.[assetId] ?? fallback;
}

export function getThemeShadowStyle(kind: ShadowKind): ThemeShadowStyle {
  return getCurrentGameTheme().shadow[kind];
}

function readInitialThemeId(): GameThemeId {
  const envTheme = import.meta.env.VITE_GAME_THEME as string | undefined;
  if (envTheme === 'gothic' || envTheme === 'forest-moving') return envTheme;
  return 'forest-moving';
}
